import { useCallback, useEffect, useRef } from 'react';

import { useInvoiceController } from 'api/controllers/InvoiceController';

import { Invoice, InvoiceProductPatch, InvoiceSection } from 'openapi';

import { round2 } from 'utils/helpers/moneyHelper';

/** Debounce window that coalesces rapid line-item edits into a single PUT. */
export const SAVE_DELAY_MS = 500;

export interface LineItemSaveRow {
  /** Existing product id; undefined marks a new row to create (appended last). */
  id?: number;
  /**
   * Temp key of a not-yet-persisted row, used by the caller to carry edits
   * typed on the pending row onto the product created for it.
   */
  tempKey?: string;
  description: string;
  /**
   * Null clears the quantity on the server (InvoiceProductPatch.quantity is
   * number | null): a line with no quantity contributes no total.
   */
  quantity: number | null;
  /**
   * Typed unit price in the displayed basis: EUR when the row has an implied
   * rate, otherwise the original-currency amount (no conversion available).
   */
  unitPriceEur: number;
  /**
   * The row's own assigned expense type; null sends an untyped row (EPN-523
   * allows rows without a type until the document is sent for approval).
   */
  expenseTypeId: number | null;
  /**
   * EUR per original-currency unit (product.unitPriceEur / product.unitPrice).
   * Null when the row has no EUR rate, in which case the typed value is sent
   * through unconverted.
   */
  impliedRate: number | null;
}

export const buildProductsPatch = (
  rows: LineItemSaveRow[]
): InvoiceProductPatch[] =>
  rows.map((row) => ({
    // desired-state semantics (EPN-523): a row without an id creates a new
    // product appended at the end; an existing id absent from the payload
    // deletes that product, so the id key is omitted entirely for creates
    ...(row.id != null ? { id: row.id } : {}),
    description: row.description,
    quantity: row.quantity,
    unitPrice: row.impliedRate
      ? round2(row.unitPriceEur / row.impliedRate)
      : row.unitPriceEur,
    expenseTypeId: row.expenseTypeId
  }));

interface UseLineItemsSectionSaveParams {
  companyId?: number;
  invoiceId?: number;
  /** Returns the current editable rows; the synthetic row must be excluded. */
  getRows: () => LineItemSaveRow[];
  /**
   * Called with the authoritative invoice returned by the section PUT together
   * with the rows that were persisted, so the caller can retire local edits
   * that the server has now acknowledged.
   */
  onInvoiceUpdated: (invoice: Invoice, savedRows: LineItemSaveRow[]) => void;
}

export const useLineItemsSectionSave = ({
  companyId,
  invoiceId,
  getRows,
  onInvoiceUpdated
}: UseLineItemsSectionSaveParams) => {
  const { saveInvoiceSectionData } = useInvoiceController();

  // Kept in a ref so a pending debounced save always reads the latest values
  // without re-arming the timer on every render.
  const stateRef = useRef({ companyId, invoiceId, getRows, onInvoiceUpdated });

  useEffect(() => {
    stateRef.current = { companyId, invoiceId, getRows, onInvoiceUpdated };
  });

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const allowEmptyRef = useRef(false);
  // Saves are serialized: a PUT never starts while another is in flight, and
  // changes made during the flight are re-saved afterwards. This keeps every
  // response authoritative for the exact rows it was dispatched with - with
  // desired-state semantics, overlapping PUTs could create or delete rows
  // twice (EPN-523).
  const inFlightRef = useRef(false);
  const dirtyRef = useRef(false);

  const runSave = useCallback(async (): Promise<boolean> => {
    if (inFlightRef.current) {
      dirtyRef.current = true;
      return false;
    }
    inFlightRef.current = true;
    try {
      const {
        getRows: currentRows,
        onInvoiceUpdated: apply,
        ...ids
      } = stateRef.current;
      const allowEmpty = allowEmptyRef.current;
      allowEmptyRef.current = false;
      if (!ids.companyId || !ids.invoiceId) {
        return false;
      }

      const rows = currentRows();
      if (rows.length === 0 && !allowEmpty) {
        return false;
      }

      const payload = { products: buildProductsPatch(rows) };

      try {
        const response = (await saveInvoiceSectionData(
          ids.companyId,
          ids.invoiceId,
          InvoiceSection.PRODUCTS,
          payload
        )) as Invoice;

        // The response is authoritative for the dispatched rows. Unsaved edits
        // made while the request was in flight are newer than this snapshot:
        // applying it does not clobber them because the caller retires only
        // the edits the snapshot acknowledged.
        apply(response, rows);
        return true;
      } catch {
        // The interceptor already showed the error toast; local values are kept
        // so the user can retry by editing again.
        return false;
      }
    } finally {
      inFlightRef.current = false;
      if (dirtyRef.current) {
        dirtyRef.current = false;
        void runSave();
      }
    }
  }, [saveInvoiceSectionData]);

  const scheduleSave = useCallback(
    (options?: { allowEmpty?: boolean }) => {
      allowEmptyRef.current = Boolean(options?.allowEmpty);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        void runSave();
      }, SAVE_DELAY_MS);
    },
    [runSave]
  );

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  /**
   * Immediate save that bypasses the debounce (EPN-607 bulk apply) but keeps
   * the same serialization as scheduleSave: while a PUT is in flight it is
   * deferred as a dirty re-save carrying the latest rows. Resolves true only
   * when a PUT carrying the current rows succeeded.
   */
  const saveNow = useCallback((): Promise<boolean> => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    return runSave();
  }, [runSave]);

  return { scheduleSave, saveNow };
};
